import { ReactNode } from 'react';

import {
  Backdrop,
  Button,
  CircularProgress,
  Link,
  Tooltip,
  styled,
} from '@material-ui/core';

import {
  RiForbidLine as Block,
  RiErrorWarningLine as PriorityHighIcon,
} from '@remixicon/react';
import isError from 'lodash/isError';

import { ListEmptyStateMui as ListEmptyState } from '../ListEmptyState';
import { useFilters } from './provider';

export type FilterListEmptyStateMuiProps = {
  onCreate?: () => void;
  createDisabledReason?: string;
  type: string;
  typePlural?: string;
  description?: ReactNode;
  filterDescription?: ReactNode;
  className?: string;
  loading?: boolean;
  error?: Error | string | null;
};

const AbsoluteBackdrop = styled(Backdrop)({
  position: 'absolute',
  zIndex: 1,
  pointerEvents: 'none',
});

export const FilterListEmptyStateMui = ({
  className,
  type,
  typePlural = `${type}s`,
  onCreate,
  createDisabledReason,
  loading = false,
  error,
  description:
    propDescription = `When you create ${typePlural} they will show up here.`,
  filterDescription = 'Tweak your filters and try again.',
}: FilterListEmptyStateMuiProps) => {
  const { isDefaultFilterState, resetFilters } = useFilters();

  let title = isDefaultFilterState
    ? `We couldn't find any ${typePlural}`
    : `No ${typePlural} matching your criteria`;
  let description = isDefaultFilterState ? propDescription : filterDescription;
  let actions = isDefaultFilterState ? (
    onCreate && (
      <Tooltip title={createDisabledReason ?? ''}>
        <div>
          <Button
            variant="outlined"
            disabled={(createDisabledReason ?? '').length !== 0}
            onClick={onCreate}
          >
            Create {type}
          </Button>
        </div>
      </Tooltip>
    )
  ) : (
    <Button variant="outlined" onClick={resetFilters}>
      Reset filters
    </Button>
  );

  let supportSnippet;
  let icon;

  if (error) {
    const errorMessage = isError(error) ? error.message : error;
    const permissionError = errorMessage.includes('permission');
    if (permissionError) {
      title = 'Missing permissions';
      description = (
        <span>
          You don&apos;t have the necessary permissions to view {typePlural}.
          Contact your Confidence administrator to gain access.
        </span>
      );
      icon = Block;
    } else {
      title = `Failed to get ${typePlural}`;
      description = (
        <span>
          We are unable to display {typePlural} right now due to the following
          error: <br />
          &quot;{errorMessage}&quot;
        </span>
      );
      supportSnippet = (
        <span>
          Still having issues?{' '}
          <Link
            underline="always"
            href={[
              'mailto:confidence@spotify.com?',
              `subject=Support request: Failed to get ${typePlural}`,
              `&body=Page: ${encodeURIComponent(
                global.window.location.href,
              )}%0D%0AError: ${encodeURIComponent(
                errorMessage,
              )}%0D%0AAdditional details:`,
            ].join('')}
            target="_blank"
          >
            Contact support
          </Link>
          .
        </span>
      );
      icon = PriorityHighIcon;
      actions = (
        <Button
          color="secondary"
          variant="outlined"
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
      );
    }
  }

  return (
    <ListEmptyState
      className={className}
      title={title}
      description={description}
      supportSnippet={supportSnippet}
      icon={icon}
      actions={actions}
    >
      <AbsoluteBackdrop open={loading}>
        <CircularProgress />
      </AbsoluteBackdrop>
    </ListEmptyState>
  );
};
